import { ApiRequestError, type ApiResponse } from '@/services/api'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseApiQueryOptions<T> {
  enabled?: boolean
  initialData?: T
  onSuccess?: (data: T) => void
  onError?: (error: ApiRequestError) => void
  refetchInterval?: number
}

export interface UseApiQueryResult<T> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  error: ApiRequestError | null
  refetch: () => Promise<void>
  reset: () => void
}

// Round-9 H12: queryFn may optionally accept an AbortSignal so domain APIs
// can opt-in to cancelling in-flight fetches. Legacy `() => Promise<…>`
// callers continue to work unchanged — the signal is passed only when the
// supplied function declares ≥ 1 argument (`fn.length >= 1`).
export type ApiQueryFn<T> = (() => Promise<ApiResponse<T>>) | ((signal: AbortSignal) => Promise<ApiResponse<T>>)

/**
 * Shared 401 handler for both hooks below. Two guards, both load-bearing:
 *
 *  1. `hadToken` — a 401 means "your session expired", which only applies if the
 *     caller actually HAD a session. A guest with no token (e.g. an anonymous
 *     viewer on a public dashboard) hitting an expected 401 on an optional
 *     authed sub-resource must NOT be ejected to login. See
 *     fix/public-dashboard-anon-groups-401.
 *  2. `gisify_refresh_token` — by the time a 401 surfaces here the api client
 *     has already run ONE silent refresh round (services/api/client.ts), and the
 *     auth clients drop the stored refresh token exactly when the provider
 *     DEFINITIVELY rejected it. Surviving refresh material therefore means the
 *     refresh failed TRANSIENTLY (offline, 5xx, throttle) — tearing the session
 *     down here would force a re-login on a session that was never invalid, so
 *     let the error surface to the caller instead. Without this the client's
 *     preserve-on-transient guard is inert for the ~200 hook call sites, which
 *     is where nearly every authed request in the app actually originates.
 *
 * Kept as one function so the two copies can't drift apart again.
 */
function handleUnauthorized(): void {
  const hadToken = !!localStorage.getItem('gisify_token')
  const canStillRecover = !!localStorage.getItem('gisify_refresh_token')

  if (canStillRecover) return

  localStorage.removeItem('gisify_token')
  localStorage.removeItem('gisify_user')

  if (hadToken && window.location.pathname !== '/auth/login') {
    window.location.href = '/auth/login'
  }
}

export function useApiQuery<T>(queryFn: ApiQueryFn<T>, options: UseApiQueryOptions<T> = {}): UseApiQueryResult<T> {
  const { enabled = true, initialData, onSuccess, onError, refetchInterval } = options

  const [data, setData] = useState<T | undefined>(initialData)
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<ApiRequestError | null>(null)

  const queryFnRef = useRef(queryFn)
  queryFnRef.current = queryFn

  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  // Round-9 H12: AbortController owned at the hook level. Each fetch creates
  // a fresh controller; unmount, enabled flip, and refetch all abort the
  // previous controller before issuing the next request. The legacy
  // `cancelled` flag stays as belt-and-braces against setState-after-unmount
  // when a queryFn doesn't honor the signal.
  const abortRef = useRef<AbortController | null>(null)

  const fetchData = useCallback(async () => {
    // Abort any in-flight request before kicking off the next one.
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    let cancelled = false
    setIsLoading(true)
    setError(null)

    try {
      const fn = queryFnRef.current
      // `fn.length >= 1` → signal-accepting variant. Otherwise call legacy
      // zero-arg shape so existing callers keep working.
      const response =
        fn.length >= 1
          ? await (fn as (signal: AbortSignal) => Promise<ApiResponse<T>>)(controller.signal)
          : await (fn as () => Promise<ApiResponse<T>>)()

      if (controller.signal.aborted) {
        cancelled = true

        return
      }

      setData(response.data)
      onSuccessRef.current?.(response.data)
    } catch (err) {
      if (controller.signal.aborted) {
        cancelled = true

        return
      }

      const apiError =
        err instanceof ApiRequestError
          ? err
          : new ApiRequestError(err instanceof Error ? err.message : 'Unknown error', 'UNKNOWN', 0)

      setError(apiError)
      onErrorRef.current?.(apiError)

      if (apiError.status === 401) {
        handleUnauthorized()
      }
    } finally {
      if (!cancelled) {
        setIsLoading(false)
      }
    }
  }, [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    setData(initialData)
    setError(null)
    setIsLoading(false)
  }, [initialData])

  useEffect(() => {
    if (enabled) {
      fetchData()
    }

    return () => {
      // Cancel any in-flight request when enabled flips off or the hook
      // unmounts. Without this, a slow API response can land on an
      // unmounted tree and trigger React's "set state on unmounted
      // component" warning.
      abortRef.current?.abort()
    }
  }, [enabled, fetchData])

  // Round-9 NOTE: we do NOT abort on queryFn ref change. Production callers
  // pass inline arrow functions (a new ref on every render), so a queryFn-
  // ref-change abort would cancel the in-flight fetch on every state update —
  // making fetches NEVER complete. The contract that matters is "abort on
  // unmount / enabled flip / explicit refetch", which the surrounding
  // useEffect already covers. The queryFnRef pattern at line 39-40 keeps
  // the latest queryFn live for the next refetch without re-firing.

  useEffect(() => {
    if (!enabled || !refetchInterval) return

    const intervalId = setInterval(fetchData, refetchInterval)

    return () => clearInterval(intervalId)
  }, [enabled, refetchInterval, fetchData])

  return {
    data,
    isLoading,
    isError: error !== null,
    error,
    refetch: fetchData,
    reset,
  }
}

export interface UseApiMutationOptions<T, V> {
  onSuccess?: (data: T, variables: V) => void
  onError?: (error: ApiRequestError, variables: V) => void
  onSettled?: (data: T | undefined, error: ApiRequestError | null, variables: V) => void
}

export interface UseApiMutationResult<T, V> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
  error: ApiRequestError | null
  mutate: (variables: V) => Promise<T | undefined>
  reset: () => void
}

export function useApiMutation<T, V = void>(
  mutationFn: (variables: V) => Promise<ApiResponse<T>>,
  options: UseApiMutationOptions<T, V> = {},
): UseApiMutationResult<T, V> {
  const { onSuccess, onError, onSettled } = options

  const [data, setData] = useState<T | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<ApiRequestError | null>(null)

  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled

  const mutate = useCallback(
    async (variables: V): Promise<T | undefined> => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await mutationFn(variables)
        setData(response.data)
        onSuccessRef.current?.(response.data, variables)
        onSettledRef.current?.(response.data, null, variables)

        return response.data
      } catch (err) {
        const apiError =
          err instanceof ApiRequestError
            ? err
            : new ApiRequestError(err instanceof Error ? err.message : 'Unknown error', 'UNKNOWN', 0)

        setError(apiError)
        onErrorRef.current?.(apiError, variables)
        onSettledRef.current?.(undefined, apiError, variables)

        if (apiError.status === 401) {
          handleUnauthorized()
        }

        return undefined
      } finally {
        setIsLoading(false)
      }
    },
    [mutationFn],
  )

  const reset = useCallback(() => {
    setData(undefined)
    setError(null)
    setIsLoading(false)
  }, [])

  return {
    data,
    isLoading,
    isError: error !== null,
    error,
    mutate,
    reset,
  }
}

export default useApiQuery
